import { ERC1155ABI, ERC20ABI } from 'core/constants'
import { useCallback, useEffect, useMemo, useState } from 'react'
import * as TokenTypes from './token.types'
import { OnChainOfferType } from './token.types'
import { useUser } from '../user'
import { normalizePrice } from 'core/helpers/contract'
import {
  useMarketplaceOffers,
  useMarketplaceSales,
  useNormalizedOffers,
} from 'context/Marketplace'
import { AssetWithBalance, Sale } from '../asset/asset.types'
import useWeb3Provider from 'hooks/useWeb3Provider'
import { useStableContracts } from 'context/Chain'
import IMarketplace from 'service/IMarketplace'
import OnchainMarketplace from 'service/IMarketplace/OnchainMarketplace'
import ZeroXMarketplace from 'service/IMarketplace/ZeroXMarketplace'
import useRouteGalleryOrDefault from 'openspace/hooks/useRoutePathGallery'
import { CypressRequirementsChecker } from 'utility/e2e'
import { ContractType } from '../contract/types/types'

export const useBatchBalances = (
  accounts: Array<string>,
  ids: Array<string>,
  nftAddress: string | undefined,
  contractType: ContractType | undefined
) => {
  const Contract = useWeb3Provider(true)?.eth.Contract
  const [batchBalances, setBatchBalances] =
    useState<TokenTypes.NftBatchBalances>({})

  useEffect(() => {
    if (!Contract) return
    if (
      accounts.length &&
      ids.length &&
      nftAddress &&
      (contractType === ContractType.RarumNFT ||
        contractType === ContractType.Collectibles)
    ) {
      const contract = new Contract(ERC1155ABI, nftAddress)
      const balances: TokenTypes.NftBatchBalances = {}

      contract.methods
        .balanceOfBatch(accounts, ids)
        .call()
        .then((result: Array<string>) =>
          result.forEach((balance: string, index: number) => {
            if (!(accounts?.[index] in balances)) {
              balances[accounts?.[index]] = {
                balances: [],
                tokens: [],
              }
            }
            balances[accounts?.[index]].balances?.push(balance)
            balances[accounts?.[index]].tokens?.push(ids[index])
          })
        )
      setBatchBalances(balances)
    }
  }, [accounts, nftAddress, Contract])

  return { batchBalances }
}

export const useOnChainOffers = (marketplace?: IMarketplace) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [offers, setOffers] = useState<OnChainOfferType[] | null>(null)
  const [fee, setFee] = useState(0)
  const contractConfig = useRouteGalleryOrDefault()

  const refreshOffers = useCallback(
    async function () {
      if (marketplace) {
        let offers!: Awaited<ReturnType<IMarketplace['getOffersForCollection']>>
        let fee!: Awaited<ReturnType<IMarketplace['getFeeForCollection']>>
        if (marketplace instanceof OnchainMarketplace) {
          const nftAddress = contractConfig.marketplace!.address
          offers = await marketplace.getOffersForCollection(nftAddress)
          fee = await marketplace.getFeeForCollection(nftAddress)
        }
        if (marketplace instanceof ZeroXMarketplace) {
          offers = await marketplace.getOffersForCollection(contractConfig.id)
          fee = await marketplace.getFeeForCollection(contractConfig.id)
        }
        setFee(fee)
        setOffers(offers)
        setLoading(false)
      }
      CypressRequirementsChecker.hasMarketplaceSetWhenSearchingOffers(
        marketplace
      )
    },
    [contractConfig, marketplace]
  )

  useEffect(() => {
    setOffers([])
    setLoading(false)
  }, [marketplace])

  useEffect(() => {
    refreshOffers()
  }, [refreshOffers])

  const { offersWithUnits, refreshSales } = useMarketplaceSales(offers)
  const normalizedOffers = useNormalizedOffers(offersWithUnits)

  return {
    loading,
    offers: normalizedOffers,
    fee,
    refreshOffers,
    refreshOffersSoldUnit: () => refreshSales(),
  }
}

export const useStableBalance = (wallet?: string) => {
  const Contract = useWeb3Provider(true)?.eth.Contract
  const [stableBalance, setStableBalance] = useState<{
    [stable: string]: number
  }>({})
  const stables = useStableContracts()
  const updateBalance = useCallback(
    function updateBalance() {
      if (!wallet) return
      Object.entries(stables).forEach(([symbol, stable]) => {
        if (stable.contract.options.address && Contract) {
          const stableContract = new Contract(
            ERC20ABI,
            stable.contract.options.address
          )
          stableContract.methods
            .balanceOf(wallet)
            .call()
            .then((result: number) => {
              setStableBalance((prev) => ({
                ...prev,
                [symbol]: normalizePrice(result, stable.decimals),
              }))
            })
        }
      })
    },
    [wallet, Contract, stables]
  )

  return {
    stableBalance,
    updateBalance,
  }
}

/**
 * Returns the amount of items available for sale
 */
export const useRemainingAvailableTokens = (
  asset?: AssetWithBalance | null,
  offerId?: Sale['offerId']
): {
  availableForSale?: number
  latestOfferId?: string
  latestOffer?: Sale
} => {
  const { sales } = useMarketplaceOffers()
  const { profile } = useUser()
  return useMemo(() => {
    if (!asset || !sales.items) return {}

    const myAssetSales = sales.items?.filter((i) => {
      return (
        i.wallet.toLowerCase() === profile?.wallet?.toLowerCase() &&
        i.offerId !== offerId &&
        i.assetId === asset.id &&
        asset.tokenId === i.tokenId
      )
    })

    const tokenSales = myAssetSales?.reduce((r, i) => r + 1, 0) || 0

    const balanceForToken = asset.balance
    const latestSale =
      myAssetSales && myAssetSales.length > 0
        ? myAssetSales[myAssetSales.length - 1]
        : undefined
    const ret = {
      availableForSale:
        typeof balanceForToken === 'number'
          ? balanceForToken - tokenSales
          : undefined,
      latestOfferId: latestSale?.offerId,
      latestOffer: latestSale,
    }

    return ret
  }, [asset, sales.items, profile?.wallet, offerId])
}
